import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { EnvelopeIcon, LockClosedIcon, UserIcon } from '@heroicons/react/24/outline';
import { useHeader } from '../../contexts/HeaderContext';
import { useTranslation } from 'react-i18next';

const RegisterPage: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { setHeaderTheme } = useHeader();

    useEffect(() => {
        setHeaderTheme({
            bgColor: 'bg-gradient-to-b from-blue-500/95 to-blue-500/80 backdrop-blur-sm',
            textColor: 'text-white'
        });
        
        return () => {
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // 1. 创建用户
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            
            // 2. 保存用户信息到 Firestore
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                email,
                username,
                role: 'student',
                status: 'active',
                createdAt: new Date().toISOString()
            });

            // 3. 发送验证邮件
            await sendEmailVerification(userCredential.user, {
                url: window.location.origin + '/#/profile',
            });

            // 4. 跳转到登录页并显示提示
            navigate('/profile', { 
                state: { 
                    message: t('auth.registerPage.success.message'),
                    type: 'success'
                } 
            });
        } catch (error: any) {
            switch (error.code) {
                case 'auth/email-already-in-use':
                    setError(t('auth.registerPage.errors.emailInUse'));
                    break;
                case 'auth/invalid-email':
                    setError(t('auth.registerPage.errors.invalidEmail'));
                    break;
                case 'auth/weak-password':
                    setError(t('auth.registerPage.errors.weakPassword'));
                    break;
                default:
                    setError(t('auth.registerPage.errors.default'));
                    console.error('Registration error:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-200 via-blue-400 to-blue-500">
            <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:32px_32px]" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-transparent" />
            <div className="relative min-h-screen flex justify-center pt-48">
                <div className="w-full max-w-md px-4">
                    {/* Logo 区域 */}
                    <div className="text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-blue-800">
                            {t('auth.registerPage.title')}
                            <span className="block text-sm font-normal text-gray-600 mt-1">
                                {t('auth.registerPage.subtitle')}
                            </span>
                        </h2>
                    </div>

                    {/* 表单区域 */}
                    <div className="mt-8">
                        <div className="bg-white/80 backdrop-blur-xl p-8 shadow-xl ring-1 ring-black/5 rounded-2xl">
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {/* 用户名输入框 */}
                                <div>
                                    <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('auth.registerPage.usernameLabel')}
                                    </label>
                                    <div className="relative mt-2">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </div>
                                        <input
                                            id="username"
                                            name="username"
                                            type="text"
                                            required
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            className="block w-full rounded-lg border-0 py-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            placeholder={t('auth.registerPage.usernamePlaceholder')}
                                        />
                                    </div>
                                </div>

                                {/* 邮箱输入框 */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('auth.registerPage.emailLabel')}
                                    </label>
                                    <div className="relative mt-2">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </div>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="block w-full rounded-lg border-0 py-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            placeholder={t('auth.registerPage.emailPlaceholder')}
                                        />
                                    </div>
                                </div>

                                {/* 密码输入框 */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('auth.registerPage.passwordLabel')}
                                    </label>
                                    <div className="relative mt-2">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </div>
                                        <input
                                            id="password"
                                            name="password"
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="block w-full rounded-lg border-0 py-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            placeholder={t('auth.registerPage.passwordPlaceholder')}
                                        />
                                    </div>
                                </div>

                                {/* 错误提示 */}
                                {error && (
                                    <div className="rounded-lg bg-red-400/10 p-4 backdrop-blur-sm">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm text-red-300">{error}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 注册按钮 */}
                                <div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex w-full justify-center items-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 px-3 py-3 text-sm font-semibold text-white shadow-sm hover:from-blue-400 hover:to-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('auth.registerPage.registering')}
                                            </>
                                        ) : (
                                            t('auth.registerPage.registerButton')
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* 登录链接 */}
                            <p className="mt-8 text-center text-sm text-gray-600">
                                {t('auth.registerPage.hasAccount')}{' '}
                                <Link to="/login" className="font-semibold text-gray-900 hover:text-gray-800">
                                    {t('auth.registerPage.login')}
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage; 